import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import styled from 'styled-components/native'

import { Theme } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/theme'

import { PrivacyGuardControl } from './PrivacyGuardControl'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { RowView } from '../../templates/content/CommonViews'
import { ThemeProvider } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { ZoomVideoBannerInfo } from '../zoomVideoBannerInfo/ZoomVideoBannerInfo'

export type ProviderJoinSessionBannerProps = {
  sessionStartDate: Date
  sessionDuration: number
  sessionStartsIn: string
  onPress: () => void
  isFetchingToken?: boolean
  platformSuffix?: string
  showPrivacyGuard?: boolean
  privacyGuardOn?: boolean
  onPrivacyGuardChange?: (on: boolean) => void
}

const Container = styled(RowView)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing['8px'],
  justifyContent: 'space-between',
  padding: theme.spacing['16px'],
  backgroundColor: theme.colors.components.zoomVideoSession.background,
  height: '90px',
}))

const JoinButton = styled(PrimaryButton)({
  alignSelf: 'center',
})

export const ProviderJoinSessionBanner: FunctionComponent<ProviderJoinSessionBannerProps> = ({
  sessionStartDate,
  sessionDuration,
  sessionStartsIn,
  onPress = noop,
  isFetchingToken,
  platformSuffix,
  showPrivacyGuard,
  privacyGuardOn = false,
  onPrivacyGuardChange,
}) => {
  const intl = useIntl()

  const joinText = showPrivacyGuard
    ? undefined
    : sessionStartsIn === 'now'
      ? intl.formatMessage({
          defaultMessage: 'Join (starts now)',
          description: 'Button to join session that is starting now',
        })
      : intl.formatMessage(
          {
            defaultMessage: 'Join (starts in {startsIn})',
            description: 'Button to join session with countdown',
          },
          {
            startsIn: sessionStartsIn,
          },
        )

  return (
    <ThemeProvider theme={Theme.DARK}>
      <Container testID={tID('ProviderJoinSessionBanner')}>
        <ZoomVideoBannerInfo
          sessionStartDate={sessionStartDate}
          sessionDuration={sessionDuration}
          platformSuffix={platformSuffix}
        />
        {showPrivacyGuard && onPrivacyGuardChange ? (
          <PrivacyGuardControl privacyGuardOn={privacyGuardOn} onPrivacyGuardChange={onPrivacyGuardChange} />
        ) : (
          <JoinButton
            testID={tID('ProviderJoinSessionBanner-joinBtn')}
            text={joinText}
            onPress={onPress}
            disabled={isFetchingToken}
          />
        )}
      </Container>
    </ThemeProvider>
  )
}
